import { PaginationDataRes, PaginationQuery } from "types/http.type";
import {
  handleGetRequest,
  handlePatchRequest,
  handlePostRequest,
  handlePutRequest,
} from "./http.service";

import {
  KycStatus,
  User,
  UserActivity,
  UserCreate,
  UserProfile,
  UserQuery,
  UserStat,
  VerificationCheck,
} from "types/user.type";
import {
  Education,
  Guarantor,
  IWorkExperience,
  Mynda,
  MyndaCreate,
} from "types/mynda.interface";
import {
  AgencyCompanyInfo,
  AgencyPersonalDetails,
  AgencyShareHolder,
} from "types/agency.interface";
import { EmployerCreate } from "types/employer.interface";
import { PaginationParams } from "types/general.interface";
import httpClient from "libs/axios.lib";
import { BASE_URL } from "constants/env.constant";
const fileDownloader = require("js-file-download");

export const getUsers = async (query: UserQuery) => {
  const queryString = constructUserQuery(query);
  return await handleGetRequest<PaginationDataRes<User[]>>(
    `/admins/users${queryString}`
  );
};
export const getRefferedUsers = async (
  referralCode: string,
  query: UserQuery
) => {
  const queryString = constructUserQuery(query);
  return await handleGetRequest<PaginationDataRes<User[]>>(
    `/admins/referrals/${referralCode}${queryString}`
  );
};
export const getArchivedUsers = async (query: UserQuery) => {
  const queryString = constructUserQuery(query);
  return await handleGetRequest<PaginationDataRes<User[]>>(
    `/admins/archived-users${queryString}`
  );
};
export const getUserProfile = async (userId: string) => {
  return await handleGetRequest<UserProfile>(`/admins/users/${userId}/profile`);
};

export const getEducation = async (userId: string) => {
  return await handleGetRequest<Education[]>(
    `/admins/users/${userId}/education`
  );
};

export const getWorkExperience = async (userId: string) => {
  return await handleGetRequest<IWorkExperience[]>(
    `/admins/users/${userId}/work-experience`
  );
};

export const getGuarantor = async (userId: string) => {
  return await handleGetRequest<Guarantor[]>(
    `/admins/users/${userId}/guarantor`
  );
};

export const getShareholder = async (userId: string) => {
  return await handleGetRequest<AgencyShareHolder>(
    `/admins/users/${userId}/share-holders`
  );
};

export const verifyUser = async (
  userId: string,
  input: { status: string; comment: string }
) => {
  return await handlePutRequest<{ status: string; comment: string }, null>(
    `/admins/users/${userId}/verify-account`,
    input
  );
};
export const sendUserMessage = async (
  userId: string,
  input: { title: string; message: string }
) => {
  return await handlePostRequest<{ title: string; message: string }, null>(
    `/admins/users/${userId}/send-message`,
    input
  );
};
export const getUserStat = async () => {
  return await handleGetRequest<UserStat>(`/admins/user-stat`);
};

export const createAgent = async (payload: UserCreate) => {
  return await handlePostRequest<UserCreate, null>("/admins/agency", payload);
};

export const updateMynda = async (userId: string, payload: MyndaCreate) => {
  return await handlePatchRequest<MyndaCreate, null>(
    `/admins/myndas/${userId}`,
    payload
  );
};

export const updateEmployer = async (
  userId: string,
  payload: EmployerCreate
) => {
  return await handlePatchRequest<EmployerCreate, null>(
    `/admins/employers/${userId}`,
    payload
  );
};

export const updateAgency = async (
  agencyId: string,
  payload: AgencyCompanyInfo & AgencyPersonalDetails
) => {
  return await handlePatchRequest<
    AgencyCompanyInfo & AgencyPersonalDetails,
    null
  >(`/admins/agency/${agencyId}`, payload);
};

export const getActivities = async (paginationOptions: PaginationParams) => {
  return await handleGetRequest<PaginationDataRes<UserActivity[]>>(
    `/admins/activities?page=${paginationOptions.page}&limit=${paginationOptions.limit}`
  );
};

export const resendGuarantorMessage = async (userId: string) => {
  return await handlePostRequest<{}, null>(
    `/admins/guarantor/${userId}/resend-notification`,
    {}
  );
};

export const archiveUser = async (userId: string) => {
  return await handlePatchRequest<{}, null>(
    `/admins/archive-user/${userId}`,
    {}
  );
};

export const getKycReport = async (userId: string) => {
  return await handleGetRequest<
    {
      title: string;
      id: number;
      submitted: boolean;
      verified: boolean;
    }[]
  >(`/auth/users/${userId}/kyc-report`);
};

export const verifyKyc = async (userId: string, id: number, value: boolean) => {
  return await handlePostRequest<{}, null>(
    `/auth/users/${userId}/kyc-report-update?id=${id}&isVerified=${value}`,
    {}
  );
};

export const updateUserVerificationCheck = async (
  userId: string,
  check: VerificationCheck
) => {
  return await handlePostRequest<VerificationCheck, null>(
    `/admins/update-verification/users/${userId}`,
    check
  );
};

export const verifyUserGuarantor = async (guarantorId: string) => {
  return await handlePostRequest<{}, null>(
    `/admins/verify-guarantor/${guarantorId}`,
    {}
  );
};

export const getAgencyMynda = async (
  agencyId: string,
  query: PaginationQuery
) => {
  const queryString = constructUserQuery(query);
  return await handleGetRequest<PaginationDataRes<Mynda[]>>(
    `/myndas/agency/${agencyId}${queryString}`
  );
};

const constructUserQuery = (params: UserQuery) => {
  let queryString = `?page=${params.page}&limit=${params.limit}`;

  if (params.search) {
    queryString = queryString.concat(
      `&search=${encodeURIComponent(params.search)}`
    );
  }

  if (params.kycStatus) {
    queryString = queryString.concat(
      `&kycStatus=${encodeURIComponent(params.kycStatus)}`
    );
  }

  // Exact, unlike `search` — this is what makes a segment page show precisely
  // the rows its card counted.
  if (params.role) {
    queryString = queryString.concat(`&role=${encodeURIComponent(params.role)}`);
  }

  return queryString;
};

export const exportUsers = async (role: string, kycStatus: string) => {
  try {
    const { data } = await httpClient.post(
      `${BASE_URL}/admins/users/export?role=${role}&kycStatus=${kycStatus}`,
      {},
      {
        responseType: "arraybuffer",
      }
    );
    return fileDownloader(data, `${role}-users.xlsx`);
  } catch (error) {
    throw error;
  }
};
